import { useEffect, useState } from 'react';

import { useRefreshTrade } from '@/hooks/useRefreshTrade';

import { buttons } from '@/styles/buttons';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';
import { SpinningRefreshIcon } from '@/ui/SpinningRefreshIcon';

import { classNames } from '@/utils/classnames';

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('contenteditable') === 'true' ||
    target.getAttribute('contenteditable') === ''
  );
};

const isRefreshKey = (event: KeyboardEvent): boolean => {
  if (event.key !== 'r' && event.key !== 'R') return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (isEditableTarget(event.target)) return false;
  return true;
};

type RefreshButtonProps = {
  sellInputDecimal: string;
};

/** Refreshes the active quote from the button or keyboard shortcut. */
export const RefreshButton = ({ sellInputDecimal }: RefreshButtonProps) => {
  const { refresh, isRefreshing, hasActiveQuote } = useRefreshTrade(sellInputDecimal);
  const [isKeyPressed, setIsKeyPressed] = useState(false);

  useEffect(() => {
    if (!hasActiveQuote) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isRefreshKey(event)) return;
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      setIsKeyPressed(true);
      void refresh('keyboard');
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key !== 'r' && event.key !== 'R') return;
      setIsKeyPressed(false);
    };
    const onBlur = () => setIsKeyPressed(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [refresh, hasActiveQuote]);

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <button
          aria-label="refresh quote"
          className={buttons({
            size: 'small',
            iconOnly: true,
            shape: 'rounded',
            color: 'transparent',
            class: [
              'relative',
              'aria-disabled:text-token-gray-600 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent',
            ],
          })}
          type="button"
          onClick={(e) => {
            if (isRefreshing || !hasActiveQuote) {
              e.preventDefault();
              return;
            }
            void refresh('refresh_button');
          }}
          aria-disabled={isRefreshing || !hasActiveQuote}
        >
          <SpinningRefreshIcon isSpinning={isRefreshing} />
        </button>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>
        {!hasActiveQuote ? (
          <span>Quote required to refresh</span>
        ) : (
          <span className="inline-flex items-center gap-6">
            <span>Refresh</span>
            <span className="text-token-gray-800 text-xs">
              <span className="mr-1">Press</span>
              <kbd
                className={classNames(
                  'inline-flex items-center justify-center rounded-[3px] h-4.5 w-4.5 font-mono text-xs text-token-gray-1600 transition-[background-color,box-shadow]',
                  isKeyPressed
                    ? 'bg-token-gray-300 shadow-[inset_0_1.5px_0_0_rgba(15,15,55,0.10)]'
                    : 'bg-token-gray-200 shadow-15',
                )}
              >
                R
              </kbd>
            </span>
          </span>
        )}
      </MatchaTooltipContent>
      <MatchaTooltipPopover side="bottom" sideOffset={8} />
    </MatchaTooltip>
  );
};
